import { createContext, useContext, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BootLoader, Button, Card, Display, SmallCap } from '@pc/ui';
import {
  DEFAULT_PUBLIC_SITE_URLS,
  getPublicSiteUrls,
  type PublicSiteUrls,
} from '@pc/db';
import { useAuth } from './AuthProvider';
import { useActiveOrg } from './ActiveOrgProvider';
import { useShopAccess } from './useShopAccess';
import { env } from '../env';

// -------------------------------------------------------------------------
// Browse-only context
//
// Pages downstream of <RequireShopAccess /> can call useShopBrowseOnly()
// to know whether the user is signed in WITHOUT an active org. This is
// the "always-on free module" branch: the user is allowed to browse,
// follow vendors, and read product detail, but cart / checkout / message
// vendor / place order all need an org. The consuming page is expected
// to gate its primary CTA on this flag (render a "Create a workspace
// to continue" link instead of the action button).
// -------------------------------------------------------------------------

export interface ShopBrowseOnlyState {
  noOrg: boolean;
  /** Where to send the user to set up a workspace. */
  setupOrgUrl: string;
}

const BrowseOnlyCtx = createContext<ShopBrowseOnlyState>({
  noOrg: false,
  setupOrgUrl: `${DEFAULT_PUBLIC_SITE_URLS.app_url}/choose-context`,
});

export function useShopBrowseOnly(): ShopBrowseOnlyState {
  return useContext(BrowseOnlyCtx);
}

export function RequireShopAccess() {
  const { signOut, session } = useAuth();
  const { activeOrgId } = useActiveOrg();
  const access = useShopAccess();

  // Resolve the operator-app URL so the browse-only banner can deep-link
  // into /choose-context. Falls back to the env default if site_config
  // isn't reachable yet.
  const urlsQ = useQuery<PublicSiteUrls>({
    queryKey: ['public-site-urls'],
    queryFn: getPublicSiteUrls,
    initialData: DEFAULT_PUBLIC_SITE_URLS,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const urls = urlsQ.data ?? DEFAULT_PUBLIC_SITE_URLS;
  const setupOrgUrl = `${urls.app_url || env.webAppUrl}/choose-context`;

  if (!access.ready) {
    return <BootLoader label="Checking access" />;
  }

  if (!access.allowed) {
    // No org AND shop isn't a free module -- we still need them to set
    // up a workspace before browsing.
    if (!activeOrgId) {
      return (
        <Upsell
          title="Pick an organization"
          message="You need to be a member of a PeptideClients organization to browse the marketplace."
          primaryHref={setupOrgUrl}
          primaryLabel="Set up a workspace"
        />
      );
    }
    // Org present but the entitlement isn't on the plan. With the
    // is_free migration applied for shop this branch is unreachable
    // for the shop module; we keep it as the catch-all in case a
    // platform admin un-marks shop as free.
    const billingUrl = `${urls.app_url || env.webAppUrl}/settings/billing?addon=shop`;
    return (
      <Upsell
        title="Shop access not on this plan"
        message="Your active organization doesn't have the Shop add-on enabled. Add Shop to your plan to browse the peptide vendor marketplace, message vendors, and follow your favorites."
        primaryHref={billingUrl}
        primaryLabel="Add Shop to my plan"
        secondaryAction={
          <Button variant="ghost" onClick={signOut}>
            Sign out ({session?.user.email})
          </Button>
        }
      />
    );
  }

  return (
    <BrowseOnlyCtx.Provider value={{ noOrg: access.noOrg, setupOrgUrl }}>
      {access.noOrg ? <BrowseOnlyBanner setupOrgUrl={setupOrgUrl} /> : null}
      <Outlet />
    </BrowseOnlyCtx.Provider>
  );
}

/**
 * Sticky banner shown above the shell content when the signed-in user
 * hasn't bootstrapped an org yet. They can browse vendors freely; the
 * cart and checkout pages render their own "create a workspace"
 * prompts as the user attempts the action.
 */
function BrowseOnlyBanner({ setupOrgUrl }: { setupOrgUrl: string }) {
  return (
    <div
      role="status"
      className="bg-linen border-b border-rule px-4 py-2 text-xs sm:text-sm text-ink-2 flex flex-wrap items-center justify-between gap-2"
    >
      <p>
        You're browsing in <strong>preview mode</strong>. Set up a workspace
        to message vendors, save a cart, and place orders.
      </p>
      <a
        href={setupOrgUrl}
        className="underline decoration-rule-strong hover:text-ink whitespace-nowrap"
      >
        Set up a workspace &rarr;
      </a>
    </div>
  );
}

function Upsell({
  title,
  message,
  primaryHref,
  primaryLabel,
  secondaryAction,
}: {
  title: string;
  message: string;
  primaryHref: string;
  primaryLabel: string;
  secondaryAction?: ReactNode;
}) {
  return (
    <main className="min-h-screen grid place-items-center bg-paper px-4">
      <div className="w-full max-w-lg">
        <div className="mb-8 text-center">
          <SmallCap>PeptideClients Shop</SmallCap>
          <Display size="md" className="mt-2">
            {title}
          </Display>
        </div>
        <Card>
          <p className="text-sm text-ink-2">{message}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={primaryHref}>
              <Button>{primaryLabel}</Button>
            </a>
            {secondaryAction}
          </div>
        </Card>
      </div>
    </main>
  );
}
